import { useEffect, useMemo, useState } from "react";

import { postTrackingData } from "@/api/endpoints/track";
import ArrowTopRightOnSquareIcon from "@/assets/icons/arrow-top-right-on-square.svg";
import ChatBubbleIcon from "@/assets/icons/chat-bubble.svg";
import { useMessages } from "@/containers/Messages/MessageContext";
import { useEpisodes } from "@/contexts/EpisodesContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import useLastNonNullishValue from "@/hooks/useLastNonNullishValue";
import { EVENT } from "@/tracking_events";
import { Episode } from "@/types/Episode";
import { MentionCard } from "@/types/MentionCard";
import { stringifyUrl } from "@/utils";
import copyToClipboard from "@/utils/copyToClipboard";
import AlertShareExportCard from "@components/AlertShareExportCard";
import Button from "@components/Button";
import { Checkbox } from "@components/Field";
import Modal, {
  ModalContent,
  ModalDescription,
  ModalTitle,
} from "@components/Modal";
import { ModalProps } from "@components/Modal/Modal";

import styles from "./ShareModal.module.css";

interface ShareModalProps extends ModalProps {
  v4?: boolean;
}

const ShareModal = ({ v4 = false, ...modalProps }: ShareModalProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const legacyEpisodesResult = useEpisodes();
  const mentionCardsResult = useMentionCards();
  const [shouldShowKpis, setshouldShowKpis] = useState(true);
  const { danger } = useMessages();

  const shareMention = v4
    ? mentionCardsResult.shareMention
    : legacyEpisodesResult.shareMention;
  const closeShare = v4
    ? mentionCardsResult.closeShare
    : legacyEpisodesResult.closeShare;

  const value = useLastNonNullishValue<Episode | MentionCard>(
    shareMention || ({} as Episode)
  );
  const show = v4
    ? (value as MentionCard)?.channel?.name
    : (value as Episode)?.show;
  const source = v4
    ? (value as MentionCard)?.episode?.title
    : (value as Episode)?.source;

  const shareUrl = useMemo(() => {
    if (!value?.share_url) {
      return "";
    }

    return stringifyUrl({
      url: value.share_url,
      query: {
        kpis: shouldShowKpis,
        sentiment: shouldShowKpis,
      },
    });
  }, [value?.share_url, shouldShowKpis]);

  useEffect(() => {
    let mounted = true;

    if (isCopied) {
      setTimeout(() => {
        if (mounted) setIsCopied(false);
      }, 3000);
    }

    return () => {
      mounted = false;
    };
  }, [isCopied]);

  const track = (event: string) => {
    postTrackingData(event, {
      url: shareUrl,
      source: source,
    });
  };

  const handleClipboardCopy = async () => {
    track(EVENT.SHARE_MENTION_COPIED);

    try {
      await copyToClipboard(shareUrl);
      setIsCopied(true);
    } catch {
      danger({
        title: "",
        message: L.MENTIONS_SHARE_COPY_TO_CLIPBOARD_FAILED,
      });
    }
  };

  return shareMention ? (
    <Modal
      data-testid="share-modal"
      className={styles.modal}
      show={shareMention !== null}
      onHide={closeShare}
      {...modalProps}
    >
      <ModalTitle className={styles.title}>
        {L.SHARE}{" "}
        <q>
          {show} - {source}
        </q>
      </ModalTitle>

      <ModalDescription>{L.SHARE_MODAL_SHARE_TEXT}</ModalDescription>

      <ModalContent>
        <AlertShareExportCard.Root>
          <AlertShareExportCard.Left>
            <AlertShareExportCard.Icon icon={ChatBubbleIcon} />
            <AlertShareExportCard.LeftText>
              <AlertShareExportCard.Title>
                {L.SHARE_MODAL_MENTION_PUBLIC_LINK}
              </AlertShareExportCard.Title>
              <Checkbox
                label={L.SHARE_WITH_ALL_KPIS}
                checked={shouldShowKpis}
                onChange={(e) => setshouldShowKpis(e.target.checked)}
              />
            </AlertShareExportCard.LeftText>
          </AlertShareExportCard.Left>

          <AlertShareExportCard.Right>
            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="share-mention-link-preview"
              onClick={() => {
                track(EVENT.SHARE_MENTION_CLICKED);
              }}
            >
              <ArrowTopRightOnSquareIcon width="1.25rem" height="1.25rem" />
            </a>

            <Button onClick={handleClipboardCopy} className="ml-3" size="md">
              {isCopied
                ? L.SHARE_MODAL_COPY_URL_SUCCESS
                : L.SHARE_MODAL_COPY_URL}
            </Button>
          </AlertShareExportCard.Right>
        </AlertShareExportCard.Root>
      </ModalContent>
    </Modal>
  ) : null;
};

export default ShareModal;
